import React, {
  useState, useContext, useEffect,
} from 'react';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import {
  Link, useParams, useHistory, useLocation,
} from 'react-router-dom';
import {
  Button,
} from '@mui/material';
import { UnfoldMore } from '@mui/icons-material/';
// eslint-disable-next-line import/no-extraneous-dependencies
import {
  User, KeyRound, MonitorSmartphone, ScanFace, CalendarClock, AlertTriangle, Pencil,
} from 'lucide-react';
import NavigationLayout from '../../components/NavigationLayout';
import API from '../../api/endpoints';
import { sendRequest } from '../../api/shootAPI';
import { UserContext } from '../../contexts/User';
import { NotificationContext } from '../../contexts/Notification';
import ChangePasswordModal from './ChangePasswordModal';
import ChangeDetailsModal from './ChangeDetailsModal';
import WorkingHoursUpdateModal from './WorkingHoursUpdateModal';
import ReportingManagerModal from './ReportingManagerModal';
import FaceRegister from './FaceRegister';
import Avatar from '../../components/avatar';
import ViewActivityTable from './ViewActivityTable';
import {
  getDatesInRange, getWeekRange, convertSecToTimeFormat, currentMonthWeekRange,
  formatDate,
} from '../../utils/time';
import CustomTable from '../../components/table/CustomTable';
import ViewIssueModal from '../alerts/ViewIssueModal';
import { getMemberRoleLevel } from '../../utils/permissions';
import { accountTypeLevel, PRIMARY_OWNER, WORKSPACE_ADMIN } from '../../constants/accountTypes';
import './account.css';

const _ = require('lodash');

const TABLE_HEADERS = [
  {
    label: 'Alert Type',
    sortable: true,
    sortIcon: UnfoldMore,
    sortKey: 'category',
    colSpan: 1,
  },
  {
    label: 'Issue Description',
    sortable: false,
    sortIcon: UnfoldMore,
    colSpan: 2,
  },
  {
    label: 'Status',
    sortable: true,
    sortIcon: UnfoldMore,
    sortKey: 'status',
    colSpan: 0.5,
  },
  {
    label: 'Acknowledged',
    sortable: true,
    sortIcon: UnfoldMore,
    sortKey: 'acknowledged',
    colSpan: 1,
  },
  {
    label: 'Date',
    sortable: true,
    sortIcon: UnfoldMore,
    sortKey: 'date',
    colSpan: 0.5,
  },
  {
    label: 'Action',
    colSpan: 0.5,
  },
];

const CustomTableStyles = {
  container: {
    marginTop: 1,
  },
  header: {
    backgroundColor: 'var(--dark-10)',
    fontSize: '14px',
    '.MuiTableSortLabel-iconDirectionAsc': {
      transform: 'rotate(0deg) !important',
    },
  },
  row: {
    '&:nth-of-type(odd)': {
      backgroundColor: '#fff !important',
    },
    '&:hover': {
      backgroundColor: 'var(--dark-10) !important',
    },
  },
  cell: {
    button: {
      textWrap: 'nowrap',
      color: 'var(--dark-2)',
      borderColor: 'var(--dark-4) !important',
      '&:disabled': {
        color: 'var(--dark-5)',
        borderColor: 'var(--dark-7) !important',
        cursor: 'not-allowed',
        pointerEvents: 'all !important',
      },
    },
  },
};

// Real platform brand logos for the Device Information row (FontAwesome brand paths)
// eslint-disable-next-line max-len
const APPLE_PATH = 'M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z';
// eslint-disable-next-line max-len
const WINDOWS_PATH = 'M0 93.7l183.6-25.3v177.4H0V93.7zm0 324.6l183.6 25.3V268.4H0v149.9zm203.8 28L448 480V268.4H203.8v177.9zm0-380.6v180.1H448V32L203.8 65.7z';

function PlatformLogo({ platform }) {
  if (platform === 'darwin') {
    return (
      <svg viewBox="0 0 384 512" width="15" height="15" fill="#0e1e40" aria-label="macOS" role="img">
        <path d={APPLE_PATH} />
      </svg>
    );
  }
  if (platform === 'win32') {
    return (
      <svg viewBox="0 0 448 512" width="15" height="15" fill="#00A4EF" aria-label="Windows" role="img">
        <path d={WINDOWS_PATH} />
      </svg>
    );
  }
  return <MonitorSmartphone size={15} className="text-ink-500" />;
}

PlatformLogo.propTypes = {
  platform: PropTypes.string,
};

const cardClass = 'rounded-xl bg-white border border-surface-2 shadow-[0_1px_2px_rgba(14,30,64,0.04)] p-5';
// Header row sits flush; content below gets a fixed 16px gap (contentClass).
const headerRowClass = 'flex items-center justify-between gap-2';
const cardHeaderGroup = 'flex items-center gap-2 text-base font-semibold text-ink-900';
const contentClass = 'mt-4';
const editBtnClass = 'inline-flex items-center gap-1.5 px-3 py-1.5 border border-rule rounded-[8px] '
  + 'text-sm font-medium text-ink-900 bg-white hover:bg-gray-50 transition';
const badgeBase = 'ml-1 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium';
const badgeOk = `${badgeBase} bg-[#ecfdf3] text-[#008a2e]`;
const badgeErr = `${badgeBase} bg-[#fff0f0] text-[#e60000]`;
// Width fixed; height stretches to the card so the image never grows the card
const faceImgClass = 'rounded-lg w-44 object-cover border border-rule';
const headerCardClass = 'rounded-xl bg-white border border-surface-2 '
  + 'shadow-[0_1px_2px_rgba(14,30,64,0.04)] p-5 flex items-center justify-between gap-4';

function Account() {
  const {
    activeWorkspace, member,
  } = useContext(UserContext);
  const { displayNotification } = useContext(NotificationContext);
  const [user, setUser] = useState({});
  const [formData, setFormData] = useState({});
  const [isProfilePage, setIsProfilePage] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showChangeDetailsModal, setShowChangeDetailsModal] = useState(false);
  const [showFaceIdSetupModal, setShowFaceIdSetupModal] = useState(false);
  const [showReportingManagerModal, setShowReportingManagerModal] = useState(false);
  const [faceIdDataPath, setFaceIdDataPath] = useState('');
  const [location, setLocation] = useState({});
  const [date, setDate] = useState(new Date());
  const [profileUpdateRequests, setProfileUpdateRequests] = useState(null);
  const [tableData, setTableData] = useState({
    dates: [], times: [], avgActivities: [], totalTime: '0:00:00', totalAvgActivity: null,
  });
  const [workspaceMember, setWorkspaceMember] = useState(member);
  const [pwdUpdateTime, setPwdUpdateTime] = useState();
  const [diffInDays, setDiffInDays] = useState(0);
  const [diffInHours, setDiffInHours] = useState(0);

  const [isOpenModal, setIsOpenModal] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const curdate = currentMonthWeekRange();
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const [selectedAlert, setSelectedAlert] = useState({});
  const [acknowledgement, setAcknowledgement] = useState([]);
  const { id } = useParams();
  const history = useHistory();
  const routerLocation = useLocation();
  // Preserve the entry-point referrer so downstream pages keep the right nav
  // item selected (e.g. arriving here from Team Engagement).
  const fromParam = new URLSearchParams(routerLocation.search).get('from');
  const fromSuffix = fromParam ? `&from=${fromParam}` : '';
  // The Schedule Adherence alert email links here with ?requestWorkingHours=1 so
  // the employee lands directly on the form rather than having to find it.
  const [showWorkingHoursModal, setShowWorkingHoursModal] = useState(
    new URLSearchParams(routerLocation.search).get('requestWorkingHours') === '1',
  );

  const onClickView = (alert) => {
    setSelectedAlert(alert);
    setIsOpenModal(true);
  };

  const handleClose = () => {
    setIsOpenModal(false);
  };

  useEffect(() => setUser(workspaceMember.user), [workspaceMember]);

  const getTableTimeAndAcitivity = (activities, dates, timezone) => {
    const times = [];
    const avgActivities = [];
    let totalSecondsElapsed = 0;
    let totalActiveSeconds = 0;
    dates.forEach((day) => {
      // get total active time for each date
      const startDay = moment.tz(day, 'MM/DD/YYYY', timezone).startOf('day').utc();
      const endDay = moment.tz(day, 'MM/DD/YYYY', timezone).endOf('day').utc();
      const initialdateActivity = { secondsElapsed: 0, activeSeconds: 0 };
      const dateActivity = activities.reduce(
        (acc, activity) => {
          // Check if the time is within the local day range
          const compareDay = moment.utc(activity.startDate);

          if (compareDay.isSameOrAfter(startDay) && compareDay.isSameOrBefore(endDay)) {
            return {
              secondsElapsed: acc.secondsElapsed + activity.secondsElapsed,
              activeSeconds: acc.activeSeconds + activity.activeSeconds,
            };
          }
          return { ...acc };
        },
        initialdateActivity,
      );
      totalSecondsElapsed += dateActivity.secondsElapsed;
      totalActiveSeconds += dateActivity.activeSeconds;
      times.push(moment.utc(dateActivity.secondsElapsed * 1000).format('H:mm:ss'));
      const avgActivity = dateActivity.secondsElapsed
        ? Number((((dateActivity.activeSeconds / dateActivity.secondsElapsed) || 0) * 100).toFixed(0))
        : null;
      avgActivities.push(avgActivity);
    });
    const totalTime = convertSecToTimeFormat(totalSecondsElapsed);
    const totalAvgActivity = totalSecondsElapsed
      ? Number((((totalActiveSeconds / totalSecondsElapsed) || 0) * 100).toFixed(0))
      : null;
    return {
      times, avgActivities, totalTime, totalAvgActivity,
    };
  };

  const getTableData = async (selectedDate) => {
    const weekRange = getWeekRange(selectedDate);
    const dates = getDatesInRange(new Date(weekRange.start), new Date(weekRange.end));
    const startOfDate = moment(dates[0]).startOf('day');
    const endOfDate = moment(dates[6]).endOf('day');
    const params = {
      start: startOfDate.format('YYYY-MM-DD HH:mm:ss'),
      end: endOfDate.format('YYYY-MM-DD HH:mm:ss'),
      timezone: activeWorkspace.timezone,
    };

    const res = await sendRequest({ method: 'get', url: API.MEMBER.activityList(workspaceMember.id), params });
    if (res.success) {
      const data = res?.data?.list || [];
      const {
        times, avgActivities, totalTime, totalAvgActivity,
      } = getTableTimeAndAcitivity(data, dates, activeWorkspace.timezone);
      setTableData({
        dates, times, avgActivities, totalTime, totalAvgActivity,
      });
    }
  };

  const handleSelectDate = async (selectedDate) => {
    setDate(selectedDate);
    getTableData(selectedDate, activeWorkspace.timezone);
  };

  const fetchMemberInfo = async (memberId) => {
    const res = await sendRequest({
      method: 'get',
      url: API.WORKSPACE.getMember(activeWorkspace.id, memberId),
    });
    if (res.success) {
      setFormData(res.data.member.user);
      setWorkspaceMember(res.data.member);
    } else if (res.status === 400) {
      displayNotification({
        message: res.data.error,
        severity: 'error',
      });
    } else if (res.status === 403) {
      displayNotification({
        message: 'You are not authorized to view this page',
        severity: 'error',
      });
      history.push('/');
    }
  };

  const fetchFaceIdData = async () => {
    const { success, data } = await sendRequest({
      method: 'get',
      url: API.USER.fetchMemberFaceIdData(workspaceMember.id),
    });
    if (success) {
      if (data.faceIdData) {
        setFaceIdDataPath(data.faceIdData.image_path);
      }
      if (data.location) {
        setLocation(data.location);
      }
      if (data.profileUpdateRequests) {
        const keyedProfileUpdateRequests = _.keyBy(data.profileUpdateRequests, 'profile_field');
        setProfileUpdateRequests(keyedProfileUpdateRequests);
      }
    }
  };

  const fetchPassword = async () => {
    const { success, data } = await sendRequest({
      method: 'get',
      url: API.USER.getpasswordupdateat,
      params: { id: activeWorkspace.id },
    });
    if (success) {
      setPwdUpdateTime(data.time);
    }
  };

  const fetchAcknowledgement = async () => {
    const res = await sendRequest({ method: 'get', url: API.WORKSPACE.getAcknowledgement() });
    if (res.success) {
      setAcknowledgement(res.data.acknowledgement);
    }
  };

  const getAlerts = async (memberId) => {
    setIsLoading(true);
    const [startDate, endDate] = curdate;
    const dates = getDatesInRange(startDate, endDate);
    const startOfDay = moment(dates[0]).utc().startOf('day')
      .format('YYYY-MM-DD HH:mm:ss');
    const endOfDay = moment(dates.at(-1)).utc().endOf('day')
      .format('YYYY-MM-DD HH:mm:ss');

    const response = await sendRequest({
      method: 'get',
      url: API.WORKSPACE.getAlertOfMember(activeWorkspace.id),
      params: {
        startOfDay,
        endOfDay,
        memberId,
      },
    });

    if (response.success) {
      const mappedAlerts = response.data?.alerts.map((alert) => {
        const id = alert?.id;
        const category = alert?.category;
        const alertMessageInternal = alert?.alertMessageInternal;
        const status = alert?.status;
        const date = alert?.date;

        let flag = false;
        acknowledgement.forEach((ack) => {
          if (ack.alertId === id) {
            flag = true;
          }
        });

        const acknowledged = flag ? 'Yes' : 'No';

        return {
          category: `${category} Alert`,
          alertMessageInternal,
          curStatus: (
            <div
              className={
                // eslint-disable-next-line no-nested-ternary
                status === 'Pending'
                  ? 'primaryBadge' : status === 'Open'
                    ? 'errorBadge' : 'successBadge'
              }
            >
              {status}
            </div>
          ),
          acknowledged,
          date: formatDate(date),
          actions: (
            <Button
              type="button"
              className="inactive-status"
              onClick={() => onClickView(alert)}
              variant="outlined"
            >
              View Issue
            </Button>
          ),
        };
      });
      setIsLoading(false);
      setAlerts(mappedAlerts);
    }
  };

  const handleMarkAsAcknowledged = async () => {
    setIsUpdating(true);
    const response = await sendRequest({
      method: 'put',
      url: API.WORKSPACE.acknowledgedByManager(selectedAlert?.id),
    });
    setIsUpdating(false);
    if (response.success) {
      setIsOpenModal(false);
      getAlerts(workspaceMember.id);
    } else {
      // handle API errors
    }
  };

  useEffect(() => {
    const timeDiff = Math.abs(new Date() - new Date(pwdUpdateTime));
    setDiffInHours(Math.floor(timeDiff / (1000 * 60 * 60)));
    setDiffInDays(Math.floor(timeDiff / (1000 * 60 * 60) / 24));
  }, [pwdUpdateTime]);

  // The member this page is meant to show: route :id for a profile, else self.
  const targetMemberId = id ? Number(id) : member?.id;

  useEffect(() => {
    // Don't fetch for the initial self-default while a profile is still loading
    if (workspaceMember?.id !== targetMemberId) return;
    handleSelectDate(date);
    getAlerts(workspaceMember.id);
    fetchAcknowledgement();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceMember]);

  useEffect(() => {
    if (workspaceMember?.id !== targetMemberId) return;
    fetchPassword();
    fetchFaceIdData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceMember]);

  useEffect(() => {
    // Clear the previous member's data so it doesn't flash before the new loads
    setFaceIdDataPath('');
    setProfileUpdateRequests(null);
    setLocation({});
    let memberId = null;
    if (id) {
      memberId = id;
      setIsProfilePage(true);
    } else {
      memberId = member.id;
    }
    fetchMemberInfo(memberId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const getDeviceInfo = (appVersionString) => {
    if (!appVersionString) return null;

    const [appVersion, platform, user, hostname] = appVersionString.split('__');

    const labelMap = {
      darwin: 'macOS',
      win32: 'Windows',
      linux: 'Linux',
    };
    const platformLabel = labelMap[platform] || platform;
    const userHost = [user, hostname].filter(Boolean).join(':');

    const text = [
      platformLabel,
      userHost,
      appVersion ? `Shoot V${appVersion}` : '',
    ]
      .filter(Boolean)
      .join(', ');

    return { platform, text };
  };

  const deviceInfo = getDeviceInfo(workspaceMember.appVersion);

  const managerUser = workspaceMember.Manager?.ManagerUser;
  const managerName = managerUser
    ? `${managerUser.firstName || ''} ${managerUser.lastName || ''}`.trim()
    : '';
  // Admins maintain the reporting line from here; a Primary owner sits at the
  // top of the org chart, so there is nothing to point them at.
  const canEditReportingManager = getMemberRoleLevel(member) >= accountTypeLevel[WORKSPACE_ADMIN]
    && workspaceMember.accountType !== PRIMARY_OWNER;

  return (
    <div>
      {showChangePasswordModal
        && (
          <ChangePasswordModal
            email={user.email}
            closeModal={() => setShowChangePasswordModal(false)}
          />
        )}

      {showChangeDetailsModal
        && (
          <ChangeDetailsModal
            formData={formData}
            address={location}
            setFormData={setFormData}
            profileUpdateRequests={profileUpdateRequests}
            fetchFaceIdData={fetchFaceIdData}
            closeModal={() => setShowChangeDetailsModal(false)}
          />
        )}

      {showFaceIdSetupModal ? (
        <FaceRegister
          closeModal={() => setShowFaceIdSetupModal(false)}
          setPath={fetchFaceIdData}
        />
      ) : null}

      {showReportingManagerModal && (
        <ReportingManagerModal
          member={workspaceMember}
          closeModal={() => setShowReportingManagerModal(false)}
          onUpdated={() => fetchMemberInfo(targetMemberId)}
        />
      )}

      {showWorkingHoursModal && (
        <WorkingHoursUpdateModal
          member={workspaceMember}
          closeModal={() => {
            setShowWorkingHoursModal(false);
            // Drop the deep-link param so a refresh does not reopen the modal.
            if (new URLSearchParams(routerLocation.search).get('requestWorkingHours')) {
              history.replace(routerLocation.pathname);
            }
          }}
        />
      )}

      <div className="account">
        <NavigationLayout
          title={isProfilePage
            ? `Account: ${formData.firstName || ''} ${formData.lastName || ''}`.trim()
            : 'My Account'}
          compact={isProfilePage}
          onBack={isProfilePage ? () => history.goBack() : undefined}
        >
          <div className="space-y-4 pb-6 pr-6">
            {/* Profile header */}
            <div className={headerCardClass}>
              <div className="flex items-center gap-4 min-w-0">
                <Avatar
                  src={formData.avatar}
                  name={(formData.firstName ? formData.firstName[0] : 'N')
                    + (formData.lastName ? formData.lastName[0] : 'A')}
                  width={64}
                  height={64}
                  color="#3d5a8a"
                  backgroundColor="#e8eef7"
                />
                <div className="min-w-0">
                  <div className="text-lg font-semibold text-ink-900 truncate">
                    {formData.firstName}
                    {' '}
                    {formData.lastName}
                  </div>
                  <div className="text-sm text-ink-500 truncate">{formData.email}</div>
                  <div className="text-xs text-ink-500 mt-0.5">
                    Last active
                    {' '}
                    {moment(workspaceMember.recentActivityTime).fromNow()}
                  </div>
                </div>
              </div>
              {!isProfilePage && (
                <button type="button" onClick={() => setShowChangeDetailsModal(true)} className={editBtnClass}>
                  <Pencil size={14} />
                  Edit
                </button>
              )}
            </div>

            {/* Basic Details · Face ID · Password · Device Information (2×2) */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Basic Details */}
              <div className={cardClass}>
                <div className={headerRowClass}>
                  <div className={cardHeaderGroup}>
                    <User size={16} className="text-brand-500" />
                    Basic Details
                  </div>
                  {!isProfilePage && (
                    <button
                      type="button"
                      onClick={() => setShowChangeDetailsModal(true)}
                      className={editBtnClass}
                    >
                      <Pencil size={14} />
                      Edit
                    </button>
                  )}
                </div>
                <div className={`${contentClass} space-y-3`}>
                  <div className="flex gap-2 text-sm">
                    <span className="text-ink-500 w-44 flex-shrink-0">Date of Birth</span>
                    <span className="text-ink-900">{formData?.birthday || '—'}</span>
                  </div>
                  <div className="flex gap-2 text-sm">
                    <span className="text-ink-500 w-44 flex-shrink-0">Current Address</span>
                    <span className="text-ink-900">{location.description || '—'}</span>
                  </div>
                  <div className="flex gap-2 text-sm">
                    <span className="text-ink-500 w-44 flex-shrink-0">Employment Start Date</span>
                    <span className="text-ink-900">{workspaceMember.employmentStartDate || '—'}</span>
                  </div>
                  {/* The reporting line drives the org chart, alert
                      acknowledgements and who approves this member's requests,
                      so it belongs on the profile — admins can correct it here. */}
                  <div className="flex gap-2 text-sm">
                    <span className="text-ink-500 w-44 flex-shrink-0">Reporting Manager</span>
                    <span className="text-ink-900" data-testid="reporting-manager-value">
                      {managerName || '—'}
                      {managerName && managerUser?.email && (
                        <span className="text-ink-500">
                          {` (${managerUser.email})`}
                        </span>
                      )}
                      {canEditReportingManager && (
                        <button
                          type="button"
                          onClick={() => setShowReportingManagerModal(true)}
                          data-testid="reporting-manager-edit"
                          className="ml-2 text-brand-600 underline"
                        >
                          Change
                        </button>
                      )}
                    </span>
                  </div>
                  {/* Schedule adherence is judged against these hours, so the
                      member needs to see them and be able to dispute them. */}
                  <div className="flex gap-2 text-sm">
                    <span className="text-ink-500 w-44 flex-shrink-0">Working Hours</span>
                    <span className="text-ink-900">
                      {workspaceMember.workingDayStartTime && workspaceMember.workingDayEndTime
                        ? [workspaceMember.workingDayStartTime, workspaceMember.workingDayEndTime]
                          .map((t) => moment(t, 'HH:mm:ss').format('h:mm A'))
                          .join(' – ')
                        : '—'}
                      {workspaceMember.workingScheduleTimezone
                        ? ` (${workspaceMember.workingScheduleTimezone})`
                        : ''}
                      {!isProfilePage && (
                        <button
                          type="button"
                          onClick={() => setShowWorkingHoursModal(true)}
                          className="ml-2 text-brand-600 underline"
                        >
                          Request change
                        </button>
                      )}
                    </span>
                  </div>
                  {profileUpdateRequests?.working_hours && (
                    <div className="text-sm text-[#e60000]">
                      Working hours change submitted. It will apply after your manager approves it.
                    </div>
                  )}
                </div>
              </div>

              {/* Current Face ID — image stretches to the card height (set by
                  Basic Details via the grid) without enlarging the card */}
              <div className={`${cardClass} flex gap-4`}>
                <div className="flex-1 min-w-0 flex flex-col">
                  <div className={headerRowClass}>
                    <div className={cardHeaderGroup}>
                      <ScanFace size={16} className="text-brand-500" />
                      Current Face ID
                      {faceIdDataPath ? (
                        <span className={badgeOk}>Verified</span>
                      ) : (
                        <span className={badgeErr}>Not Registered</span>
                      )}
                    </div>
                    {!isProfilePage && (
                      <button type="button" onClick={() => setShowFaceIdSetupModal(true)} className={editBtnClass}>
                        Update
                      </button>
                    )}
                  </div>
                  <p className={`${contentClass} text-sm text-ink-500 mb-0`}>
                    {
                      // eslint-disable-next-line no-nested-ternary
                      profileUpdateRequests?.face_id
                        ? (
                          <span className="text-[#e60000]">
                            New changes submitted. New changes will apply after approval.
                          </span>
                        )
                        : faceIdDataPath
                          ? 'Current Face ID registered and verified. Update if needed.'
                          : 'Face ID is not registered yet!'
                    }
                  </p>
                </div>
                {(faceIdDataPath || profileUpdateRequests?.face_id) && (
                  <div className="flex items-stretch gap-2 flex-shrink-0">
                    {faceIdDataPath && (
                      <img className={faceImgClass} src={faceIdDataPath} alt="Registered face" />
                    )}
                    {profileUpdateRequests?.face_id && (
                      <img
                        className={faceImgClass}
                        src={profileUpdateRequests.face_id.new_value.image_path}
                        alt="Submitted face"
                      />
                    )}
                  </div>
                )}
              </div>

              {/* Password */}
              <div className={cardClass}>
                <div className={headerRowClass}>
                  <div className={cardHeaderGroup}>
                    <KeyRound size={16} className="text-brand-500" />
                    Password
                  </div>
                  {!isProfilePage && (
                    <button
                      type="button"
                      onClick={() => setShowChangePasswordModal(true)}
                      className={editBtnClass}
                    >
                      Update
                    </button>
                  )}
                </div>
                <div className={`${contentClass} text-sm text-ink-500`}>
                  {isProfilePage
                    ? `${`${formData.firstName || ''} ${formData.lastName || ''}`.trim()}'s password was changed`
                    : 'Your password was changed'}
                  {' '}
                  {
                    // eslint-disable-next-line no-nested-ternary
                    diffInDays
                      ? `${diffInDays} day${diffInDays === 1 ? ' ago' : 's ago'}`
                      : diffInHours
                        ? `${diffInHours} hour${diffInHours === 1 ? ' ago' : 's ago'}`
                        : ' now'
                  }
                </div>
              </div>

              {/* Device Information */}
              <div className={cardClass}>
                <div className={cardHeaderGroup}>
                  <MonitorSmartphone size={16} className="text-brand-500" />
                  Device Information
                </div>
                <div className={`${contentClass} text-sm text-ink-900`}>
                  {deviceInfo ? (
                    <span className="inline-flex items-center gap-2">
                      <PlatformLogo platform={deviceInfo.platform} />
                      {deviceInfo.text}
                    </span>
                  ) : '—'}
                </div>
              </div>
            </div>

            {/* Timesheets */}
            <div className={cardClass}>
              <div className={headerRowClass}>
                <div className={cardHeaderGroup}>
                  <CalendarClock size={16} className="text-brand-500" />
                  Timesheets
                </div>
                <Link
                  className={editBtnClass}
                  to={`/members/activity/view?member=${workspaceMember.id}${fromSuffix}`}
                >
                  View Detailed Timesheets
                </Link>
              </div>
              <div className={contentClass}>
                {(tableData.times.length > 0 && tableData.avgActivities.length > 0) ? (
                  <ViewActivityTable
                    tableData={tableData}
                    date={date}
                    handleSelectDate={handleSelectDate}
                    workspaceMember={workspaceMember}
                  />
                ) : (
                  <div className="text-center text-sm text-ink-500 py-8">No timesheet data for this week.</div>
                )}
              </div>
            </div>

            {/* Recent Alerts */}
            <div className={cardClass}>
              <div className={cardHeaderGroup}>
                <AlertTriangle size={16} className="text-[#b94f3c]" />
                Recent Alerts
              </div>
              <div className={contentClass}>
                <CustomTable
                  headers={TABLE_HEADERS}
                  items={alerts}
                  loading={isLoading}
                  styles={CustomTableStyles}
                />
                {isOpenModal && (
                  <ViewIssueModal
                    selectedAlert={selectedAlert}
                    isOpen={isOpenModal}
                    handleClose={handleClose}
                    handleMarkAsAcknowledged={handleMarkAsAcknowledged}
                    isUpdating={isUpdating}
                  />
                )}
              </div>
            </div>
          </div>
        </NavigationLayout>
      </div>
    </div>
  );
}

export default Account;
